import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PenLine, Plus, Sheet, ClipboardList, X } from 'lucide-react'
import { createClient, importClients, createContact } from '../../lib/api'
import type { ClientDraft, ContactDraft, IntakeMethod, ManualMode } from './intake/types'
import { ManualIntake } from './intake/ManualIntake'
import { FileImportIntake } from './intake/FileImportIntake'
import { PasteIntake } from './intake/PasteIntake'

interface ClientIntakeProps {
  onClose: () => void
  /** Called with a toast message after a successful add/import. */
  onSuccess?: (message: string) => void
  /** Lets the file/paste steps hand off to the Onboard flow when the sheet has team/task columns. */
  onSwitchToOnboard?: () => void
}

const METHODS: {
  key: IntakeMethod
  label: string
  desc: string
  icon: typeof PenLine
}[] = [
  { key: 'manual', label: 'Manual entry', desc: 'One client, by hand', icon: PenLine },
  { key: 'import', label: 'File import', desc: 'CSV or Excel upload', icon: Sheet },
  { key: 'paste', label: 'Paste rows', desc: 'From a spreadsheet', icon: ClipboardList },
]

export function ClientIntake({ onClose, onSuccess, onSwitchToOnboard }: ClientIntakeProps) {
  const qc = useQueryClient()
  const [method, setMethod] = useState<IntakeMethod>('manual')
  const [manualMode, setManualMode] = useState<ManualMode>('quick')

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const finish = (message: string) => {
    qc.invalidateQueries({ queryKey: ['clients'] })
    onSuccess?.(message)
    onClose()
  }

  const manualMutation = useMutation({
    mutationFn: async ({ draft, contact }: { draft: ClientDraft; contact?: ContactDraft }) => {
      const client = await createClient(draft)
      if (contact) {
        // A failed contact write should not lose the created client — swallow it.
        await createContact(client.id, contact).catch(() => undefined)
      }
      return client
    },
    onSuccess: (client) => finish(`Added ${client.name}`),
  })

  const importMutation = useMutation({
    mutationFn: (drafts: ClientDraft[]) => importClients(drafts),
    onSuccess: (res) => finish(`Imported ${res.count} client${res.count === 1 ? '' : 's'}`),
  })

  const importError =
    (importMutation.error as Error | null)?.message ?? (manualMutation.error as Error | null)?.message

  return (
    <div className="quick-add-backdrop" onClick={onClose}>
      <div className="intake-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Add client">
        {/* Header */}
        <div className="intake-header">
          <span className="intake-header-icon">
            <Plus size={18} strokeWidth={2.2} />
          </span>
          <div className="intake-header-text">
            <h3>Add client</h3>
            <p>Enter one by hand, or import many from a spreadsheet.</p>
          </div>
          <button onClick={onClose} className="btn-icon intake-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="intake-body">
          {/* Method rail */}
          <nav className="intake-rail" aria-label="Intake method">
            {METHODS.map((m) => {
              const Icon = m.icon
              return (
                <button
                  key={m.key}
                  type="button"
                  className={`intake-rail-item${method === m.key ? ' intake-rail-item--active' : ''}`}
                  onClick={() => setMethod(m.key)}
                >
                  <Icon size={17} strokeWidth={2} />
                  <span className="intake-rail-item-text">
                    <span className="intake-rail-item-label">{m.label}</span>
                    <span className="intake-rail-item-desc">{m.desc}</span>
                  </span>
                </button>
              )
            })}
            <p className="intake-rail-note">
              Imports run through the same validation as a single add — nothing lands half-formed.
            </p>
          </nav>

          {/* Active method */}
          <div className="intake-content">
            {method === 'manual' && (
              <ManualIntake
                mode={manualMode}
                onModeChange={setManualMode}
                onSubmit={(payload) => manualMutation.mutate(payload)}
                isSubmitting={manualMutation.isPending}
              />
            )}
            {method === 'import' && (
              <FileImportIntake
                onImport={(drafts) => importMutation.mutate(drafts)}
                isSubmitting={importMutation.isPending}
                onSwitchToOnboard={onSwitchToOnboard}
              />
            )}
            {method === 'paste' && (
              <PasteIntake
                onImport={(drafts) => importMutation.mutate(drafts)}
                isSubmitting={importMutation.isPending}
                onSwitchToOnboard={onSwitchToOnboard}
              />
            )}

            {importError && (
              <p className="intake-errors intake-errors--block" role="alert">
                {importError}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
